import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueSchemeFormMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      forms: {
        schemeCreate: {
          title: "新建演练方案",
          generatedCodeHintLabel: "方案编号",
          generatedCodeHintValue: "系统创建后自动生成周制编号，例如 RBS-2623-001",
          nameLabel: "方案名称",
          nameRequired: "请输入方案名称",
          nameMax: "方案名称不能超过 255 个字符",
          modeLabel: "演练模式",
          modeRequired: "请选择演练模式",
          descriptionLabel: "方案说明",
          cancel: "取消",
          prevStep: "上一步",
          nextStep: "下一步",
          confirmCreate: "确认创建演练方案",
          enterWorkbench: "进入方案工作台",
          closeConfirmTitle: "确认关闭创建流程？",
          closeConfirmContent: "方案 {code} 已创建；现在关闭会保留已创建结果。",
          closeConfirmOk: "关闭并保留结果",
          closeConfirmCancel: "继续配置",
          createdTag: "已创建 {code}",
          stepBasicsTitle: "第一步 基本信息",
          stepBasicsDescription: "填写方案名称、模式与说明。",
          stepBasicsNotice:
            "先完成方案基础信息；方案编号由后端统一生成，创建后再进入工作台补齐设计细节。",
          stepConfirmTitle: "第二步 创建确认",
          stepConfirmDescription: "核对创建摘要，并正式创建方案。",
          stepConfirmNotice:
            "这一步会真实调用后端创建方案；创建完成后，继续进入工作台完善拓扑、阵营、任务评分与报告模板。",
          stepFinishTitle: "第三步 进入工作台",
          stepFinishDescription: "确认创建结果，并进入方案设计工作台。",
          stepFinishNotice:
            "方案已创建成功。下一步进入 `/schemes/:schemeCode` 工作台继续完成版本级设计，不再回到旧抽屉链路。",
          summaryCardTitle: "创建摘要",
          designSectionsTitle: "创建后将进入方案工作台",
          designSectionsDescription: "创建后将进入方案工作台，按版本继续维护以下设计区块。",
          finishCardTitle: "已创建方案",
          emptyDescription: "未填写方案说明",
        },
        schemeCopy: {
          title: "复制方案为新草稿",
          nameLabel: "新方案名称",
          nameRequired: "请输入新方案名称",
          nameMax: "方案名称不能超过 255 个字符",
          descriptionLabel: "新方案说明",
          submitText: "复制并进入详情",
        },
        schemeArchive: {
          title: "归档方案",
          confirmText:
            "确认归档方案 {name}（{code}）？归档后该方案进入终态，不能继续编辑或发布；如需继续设计，请复制为新草稿。",
          reasonLabel: "归档原因",
          reasonPlaceholder: "请输入归档原因，可留空",
          submitText: "确认归档",
        },
        schemeEdit: {
          title: "编辑方案",
          nameLabel: "方案名称",
          nameRequired: "请输入方案名称",
          nameMax: "方案名称不能超过 255 个字符",
          modeLabel: "演练模式",
          modeRequired: "请选择演练模式",
          descriptionLabel: "方案说明",
        },
      },
    },
  },
  en: {
    redblue: {
      forms: {
        schemeCreate: {
          title: "Create Scheme",
          generatedCodeHintLabel: "Scheme Code",
          generatedCodeHintValue:
            "Generated automatically after creation, for example RBS-2623-001",
          nameLabel: "Scheme Name",
          nameRequired: "Enter a scheme name",
          nameMax: "Scheme name must be 255 characters or fewer",
          modeLabel: "Activity Mode",
          modeRequired: "Select an activity mode",
          descriptionLabel: "Scheme Description",
          cancel: "Cancel",
          prevStep: "Previous",
          nextStep: "Next",
          confirmCreate: "Create Scheme",
          enterWorkbench: "Open Scheme Workbench",
          closeConfirmTitle: "Close the creation flow?",
          closeConfirmContent:
            "Scheme {code} has already been created. Closing now will keep the created result.",
          closeConfirmOk: "Close and Keep Result",
          closeConfirmCancel: "Continue Editing",
          createdTag: "Created {code}",
          stepBasicsTitle: "Step 1 Basics",
          stepBasicsDescription: "Fill in the scheme name, mode, and description.",
          stepBasicsNotice:
            "Complete the basic scheme information first. The backend generates the weekly code after creation, and the workbench handles the remaining design details.",
          stepConfirmTitle: "Step 2 Confirm and Create",
          stepConfirmDescription: "Review the summary and create the scheme for real.",
          stepConfirmNotice:
            "This step makes the real backend create request. After the scheme exists, continue in the workbench to finish topology, camps, task scoring, and report templates.",
          stepFinishTitle: "Step 3 Open Workbench",
          stepFinishDescription: "Confirm the result and continue in the scheme design workbench.",
          stepFinishNotice:
            "The scheme has been created successfully. Continue in `/schemes/:schemeCode` to finish version-level design instead of returning to the legacy drawer path.",
          summaryCardTitle: "Creation Summary",
          designSectionsTitle: "Continue in the scheme workbench",
          designSectionsDescription:
            "After creation, continue maintaining the following design sections by version in the scheme workbench.",
          finishCardTitle: "Created Scheme",
          emptyDescription: "No scheme description provided",
        },
        schemeCopy: {
          title: "Copy Scheme as New Draft",
          nameLabel: "New Scheme Name",
          nameRequired: "Enter the new scheme name",
          nameMax: "Scheme name must be 255 characters or fewer",
          descriptionLabel: "New Scheme Description",
          submitText: "Copy and Open Detail",
        },
        schemeArchive: {
          title: "Archive Scheme",
          confirmText:
            "Archive scheme {name} ({code})? After archiving, the scheme becomes terminal and can no longer be edited or published. Copy it to a new draft if you need to keep designing.",
          reasonLabel: "Archive Reason",
          reasonPlaceholder: "Enter an archive reason, or leave it empty",
          submitText: "Confirm Archive",
        },
        schemeEdit: {
          title: "Edit Scheme",
          nameLabel: "Scheme Name",
          nameRequired: "Enter a scheme name",
          nameMax: "Scheme name must be 255 characters or fewer",
          modeLabel: "Activity Mode",
          modeRequired: "Select an activity mode",
          descriptionLabel: "Scheme Description",
        },
      },
    },
  },
  ja: {
    redblue: {
      forms: {
        schemeCreate: {
          title: "プランを作成",
          generatedCodeHintLabel: "プランコード",
          generatedCodeHintValue: "作成後に自動採番されます。例: RBS-2623-001",
          nameLabel: "プラン名",
          nameRequired: "プラン名を入力してください",
          nameMax: "プラン名は 255 文字以内で入力してください",
          modeLabel: "演習モード",
          modeRequired: "演習モードを選択してください",
          descriptionLabel: "プラン説明",
          cancel: "キャンセル",
          prevStep: "前へ",
          nextStep: "次へ",
          confirmCreate: "プランを作成",
          enterWorkbench: "プラン工作台へ進む",
          closeConfirmTitle: "作成フローを閉じますか？",
          closeConfirmContent:
            "プラン {code} はすでに作成されています。ここで閉じても作成結果は保持されます。",
          closeConfirmOk: "閉じて結果を保持",
          closeConfirmCancel: "引き続き設定する",
          createdTag: "作成済み {code}",
          stepBasicsTitle: "第1步 基本情報",
          stepBasicsDescription: "プラン名、モード、説明を入力します。",
          stepBasicsNotice:
            "まずプランの基本情報を入力します。週次コードはバックエンドが生成し、残りの設計は作成後に工作台で続けます。",
          stepConfirmTitle: "第2步 作成確認",
          stepConfirmDescription: "作成サマリーを確認し、正式にプランを作成します。",
          stepConfirmNotice:
            "この手順で実際にバックエンドへ作成リクエストを送ります。作成後は工作台でトポロジー、陣営、タスク採点、レポートテンプレートを完成させます。",
          stepFinishTitle: "第3步 工作台へ進む",
          stepFinishDescription: "作成結果を確認し、プラン設計工作台へ進みます。",
          stepFinishNotice:
            "プランは正常に作成されました。旧 drawer には戻らず、`/schemes/:schemeCode` 工作台で版ごとの設計を続けてください。",
          summaryCardTitle: "作成サマリー",
          designSectionsTitle: "作成後は方案工作台で続ける",
          designSectionsDescription:
            "作成後は方案工作台に入り、以下の設計区块をバージョンごとに継続して管理します。",
          finishCardTitle: "作成済みプラン",
          emptyDescription: "プラン説明は未入力です",
        },
        schemeCopy: {
          title: "プランを新しい下書きへ複製",
          nameLabel: "新しいプラン名",
          nameRequired: "新しいプラン名を入力してください",
          nameMax: "プラン名は 255 文字以内で入力してください",
          descriptionLabel: "新しいプラン説明",
          submitText: "複製して詳細へ進む",
        },
        schemeArchive: {
          title: "プランをアーカイブ",
          confirmText:
            "プラン {name}（{code}）をアーカイブしますか？アーカイブ後は終態となり、編集や公開はできません。設計を続ける場合は新しい下書きへ複製してください。",
          reasonLabel: "アーカイブ理由",
          reasonPlaceholder: "アーカイブ理由を入力してください。空欄でも構いません",
          submitText: "アーカイブを確定",
        },
        schemeEdit: {
          title: "プランを編集",
          nameLabel: "プラン名",
          nameRequired: "プラン名を入力してください",
          nameMax: "プラン名は 255 文字以内で入力してください",
          modeLabel: "演習モード",
          modeRequired: "演習モードを選択してください",
          descriptionLabel: "プラン説明",
        },
      },
    },
  },
};
